import { createContext, useContext, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

/**
 * Two slots a wrapper can fill on the field it wraps, so a field can show where its value comes from
 * without the field itself knowing anything about inheritance, projects or fallbacks.
 *
 * Passed by context rather than by prop because the wrapper (`InheritedField` in the backoffice) is
 * handed the field as opaque children: it cannot reach inside to the label row or to the control.
 * Every field component renders whatever slot it can host and ignores the rest, so wrapping a field
 * that does not host the ghost degrades to the annotation alone instead of breaking.
 */
export interface FieldFallback {
	/**
	 * A marker beside the label, saying where this field's value comes from. Costs no vertical space,
	 * so it is the one part every wrapped field shows.
	 */
	annotation?: ReactNode;
	/**
	 * What to render inside the control's own box while the field holds nothing — the value that is
	 * live somewhere else. The control is empty in that state, so this takes space the form already
	 * reserved and the page gains no height from it.
	 */
	ghost?: ReactNode;
}

const FieldFallbackContext = createContext<FieldFallback | undefined>(undefined);

export const FieldFallbackProvider = FieldFallbackContext.Provider;

export const useFieldFallback = () => useContext(FieldFallbackContext);

/**
 * The ghost laid over a single-line control, aligned with the text the control would hold.
 *
 * `inset-0` resolves against the control's padding box, so the horizontal padding is repeated here to
 * land on the same baseline as a typed value. It never takes the pointer: clicking it has to reach
 * the control underneath, which is how an author starts writing over the fallback. And it hides while
 * that control has focus (`peer-focus`), so the caret never sits in the middle of someone else's text.
 */
export function FieldGhost(props: { children: ReactNode; className?: string }) {
	return (
		<div
			className={cn(
				'text-muted-foreground pointer-events-none absolute inset-0 flex items-center text-base peer-focus:hidden md:text-sm',
				props.className,
			)}
		>
			<span className="truncate">{props.children}</span>
		</div>
	);
}
